import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box, Card, CardContent, Grid, CircularProgress } from "@mui/material";
import { Save } from "@mui/icons-material";
import { ComponentHeader, RichTextEditor } from "components";
import { BrandButton } from "components/brand";
import { getPrivacyPolicy, updatePrivacyPolicy } from "actions/privacyPolicy";
import { clearPrivacyPolicy } from "features/privacyPolicySlice";
import { ADMIN_TEXTS } from "constants/adminTexts";
import "./PrivacyPolicy.scss";

const T = ADMIN_TEXTS.privacyPolicy;

const validationSchema = Yup.object({
  content: Yup.string()
    .required(T.contentRequired)
    .test("min-length", T.contentMinLength, (value) => {
      const text = value?.replace(/<[^>]*>/g, "").trim();
      return text && text.length >= 10;
    }),
});

export const PrivacyPolicyContent = () => {
  const dispatch = useDispatch();
  const { privacyPolicy, loading, updateLoading } = useSelector(
    (state) => state.privacyPolicy,
  );

  const formik = useFormik({
    initialValues: {
      content: privacyPolicy?.content || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      dispatch(updatePrivacyPolicy({ content: values.content }));
    },
  });

  useEffect(() => {
    dispatch(getPrivacyPolicy());
    return () => {
      dispatch(clearPrivacyPolicy());
    };
  }, [dispatch]);

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="privacy-policy-content">
      <ComponentHeader title={T.contentPageTitle} />
      <Card>
        <CardContent>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <RichTextEditor
                  value={formik.values.content}
                  onChange={(value) => formik.setFieldValue("content", value)}
                  onBlur={() => formik.setFieldTouched("content", true)}
                  placeholder={T.contentPlaceholder}
                  label={T.contentLabel}
                  required
                  error={formik.errors.content}
                  touched={formik.touched.content}
                />
              </Grid>

              <Grid item xs={12}>
                <Box className="form-footer">
                  <BrandButton
                    type="submit"
                    variant="primary"
                    disabled={updateLoading}
                    startIcon={
                      updateLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <Save />
                      )
                    }
                  >
                    {updateLoading
                      ? ADMIN_TEXTS.actions.saving
                      : ADMIN_TEXTS.common.save}
                  </BrandButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
